/**
 * useI18n - which language the site is in, and how to look a phrase up
 * ---------------------------------------------------------------------
 * ChoreConnect is used mainly by Italian speakers, at a school in Varese, and
 * was written entirely in English. This is what makes it bilingual.
 *
 * WHY THIS IS HAND-WRITTEN AND NOT react-i18next
 *
 * The same reasoning as DayPicker and the time dropdown, and it is written
 * down in DayPicker.jsx: this is a site one person maintains, the whole
 * frontend dependency list is four packages, and every package added has to be
 * kept up to date for ever. i18next plus react-i18next is two more, bringing
 * namespaces, loaders and a config file, to do something that is a lookup in
 * an object.
 *
 * Two languages is also the case a library helps with least. Italian
 * pluralises the way English does - one, or not one - so the plural rule is a
 * ternary rather than a table. If a third language ever arrives, or if
 * somebody other than the owner starts translating and wants a proper tool,
 * that is the moment to reach for a library. It is not this moment.
 *
 * HOW A PHRASE IS FOUND
 *
 *   t('nav.browse')                          -> "Browse chores" / "Cerca lavori"
 *   t('chore.pay', { amount: 15 })           -> fills in {amount}
 *   t('chore.visits', { count: 3 })          -> picks visits_one / visits_other
 *
 * A MISSING PHRASE FALLS BACK TO ENGLISH, never to a blank or to the key
 * itself. A half-translated screen showing English is usable; one showing
 * `chore.detail.title` is broken. `npm run check-translations` lists what is
 * missing - a blank space nobody notices is how a translation stays half done.
 */

import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';

import { setFormatLocale } from '../utils/format';
import en from '../i18n/en';
import it from '../i18n/it';

const DICTIONARIES = { en, it };

/**
 * The languages offered, in the order the switch shows them.
 *
 * `label` is what appears in the navbar. Two letters rather than a flag,
 * deliberately: a flag is a country and not a language, and "English" has no
 * flag that is right for a European school - the UK left the EU and the
 * stars-and-stripes would be stranger still. Two letters are also far smaller
 * on a phone, which is what most of this site is read on.
 */
export const LANGUAGES = [
  { code: 'it', label: 'IT', name: 'Italiano' },
  { code: 'en', label: 'EN', name: 'English' },
];

const STORAGE_KEY = 'cc-language';

/**
 * Which language to open in, for somebody who has never chosen.
 *
 * The browser is asked first, because a family in Varese arriving on an
 * Italian-language machine should not have to find a switch to read the site.
 * Anything that is not obviously Italian gets English, which is the language
 * the site was written in and the one every phrase definitely exists in.
 */
function detectLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && DICTIONARIES[saved]) return saved;
  } catch {
    // Private browsing can make localStorage throw rather than return null.
    // A language preference is not worth failing a page load over.
  }

  /**
   * The browser's list is IN ORDER OF PREFERENCE, and the order is the whole
   * point of it. Somebody whose machine says "English, then Italian" has said
   * they would rather read English - so searching the whole list for "it" and
   * taking it would override a preference they actually expressed. Walk it in
   * order and take the first language the site has.
   */
  const preferred = navigator.languages?.length
    ? navigator.languages
    : [navigator.language || 'en'];

  for (const tag of preferred) {
    // 'it-CH' and 'it' both mean Italian; 'en-US' and 'en-GB' both mean English.
    const base = String(tag).toLowerCase().split('-')[0];
    if (DICTIONARIES[base]) return base;
  }

  return 'en';
}

/**
 * The current language, readable from outside React.
 *
 * `services/api.js` needs it at signup - the account records which language
 * the site was being read in, because that is what decides the language of
 * every email afterwards, and the server has no browser to ask. api.js is not
 * a component and cannot use the hook, so this is the way in.
 *
 * It reads the same saved value and falls back to the same detection, rather
 * than keeping a second copy of either.
 */
export function currentLanguage() {
  return detectLanguage();
}

/** Walks 'chore.detail.title' down an object, returning undefined if it breaks. */
function lookup(dict, key) {
  return key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), dict);
}

/** Replaces {name} with values.name. Anything unmatched is left alone. */
function fill(text, values) {
  if (!values) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name) =>
    (Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : whole));
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLanguage);

  const setLang = useCallback((code) => {
    if (!DICTIONARIES[code]) return;
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // See detectLanguage - not worth throwing over.
    }
  }, []);

  /**
   * The <html lang> attribute follows the choice.
   *
   * Not decoration: it is what a screen reader uses to pick a voice, and what
   * the browser uses for hyphenation and spellcheck. Reading Italian aloud in
   * an English voice is close to unintelligible.
   *
   * It is deliberately NOT what decides date formats - see the note in
   * index.html. Chrome ignores this attribute for date controls, which is why
   * the site draws its own.
   */
  useEffect(() => {
    document.documentElement.lang = lang === 'it' ? 'it' : 'en-GB';
  }, [lang]);

  const t = useCallback(
    (key, values) => {
      const found = lookup(DICTIONARIES[lang], key);
      /**
       * English is the fallback, always - see the note at the top of the file.
       *
       * The test here used to be `typeof found === 'string'`, and that is
       * false for a PLURAL phrase, because those are objects of
       * { one, other }. So every plural on the site fell through to English
       * however the language was set, and did it silently: the Italian
       * phrases existed, check-translations counted them as present, and
       * nothing anywhere said they were never being read.
       *
       * "3 lavoretti" came out as "3 chores" on the marketplace, and the
       * suspension countdown said "2 giorni" in English inside an Italian
       * sentence, which is how it was finally noticed.
       *
       * The question is only whether THIS language has the phrase at all,
       * so ask that and nothing else.
       */
      const text = found == null ? lookup(en, key) : found;

      if (typeof text === 'object' && text !== null && values && 'count' in values) {
        const form = Number(values.count) === 1 ? text.one : text.other;
        return fill(form ?? '', values);
      }

      if (typeof text !== 'string') {
        // Only reachable if the key is wrong in BOTH dictionaries, which is a
        // typo rather than a missing translation. Showing the key is right
        // here: it is the only thing that tells you where to look.
        if (import.meta.env.DEV) console.warn(`[i18n] no phrase for "${key}"`);
        return key;
      }

      return fill(text, values);
    },
    [lang]
  );

  /**
   * The locale to hand to toLocaleDateString and friends.
   *
   * 'en-GB' rather than 'en', because the English half of this site is British
   * English for a school in Italy, and plain 'en' means American - which puts
   * the month first. Section 10b of HANDOFF.md has the whole story.
   */
  const locale = lang === 'it' ? 'it-IT' : 'en-GB';

  // Pushed into utils/format.js SYNCHRONOUSLY, in the provider body rather
  // than in an effect. Children render after their parent, so every date
  // formatted below this point is already in the right language - an effect
  // would leave the first paint in the previous one. See format.js.
  // t goes with it: "3 days ago" and "5 km away" are OUR words, not
  // something toLocaleDateString can produce, so format.js needs the
  // dictionary as well as the locale.
  setFormatLocale(locale, t);

  const value = useMemo(() => ({ lang, setLang, t, locale }), [lang, setLang, t, locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside an <I18nProvider>');
  return context;
}
